import { Alert, Spin } from "antd";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";
import { AboutPanel } from "./components/AboutPanel";
import { Controls } from "./components/Controls";
import { DataTable } from "./components/DataTable";
import { SeriesChart } from "./components/SeriesChart";
import { useBuildRetired, useCountryCells, useDark, useLand, useMapLayer, usePlaces, useSelection, useSummary, type PointPlace } from "./hooks";
import { addPoint, FULL_MESSAGE, mapReason, MAX_POINTS, metricOf, seriesOf, type Selection } from "./model";
import type { Point, Summary } from "./types";

// Leaflet needs the browser.
const MapPanel = dynamic(() => import("./components/MapPanel").then(m => m.MapPanel), {
  ssr: false,
  loading: () => <Spin />
});

/**
 * The climate metrics dashboard: one ESRS hazard's metric on a world map through the century,
 * what it means, and its numbers at up to 30 locations as a chart and a table.
 */
export const ClimateMetricsTool = () => {
  const {
    build,
    summary,
    error,
    isLoading
  } = useSummary();
  const [selection, update] = useSelection(summary);
  const places = usePlaces(build, selection?.points ?? []);
  const retired = useBuildRetired();
  if (retired) {
    return <Alert type="warning" showIcon title="Climate metrics have been updated" description="Reload the page to see the new data." />;
  }
  if (error) {
    return <Alert type="error" showIcon title="Climate metrics are not available" description={"status" in error && error.status === 403 ? "Your account does not have this tool." : "The data service did not answer. Try again shortly."} />;
  }
  if (isLoading || !summary || !selection || !build) return <Spin />;
  return <Dashboard build={build} summary={summary} selection={selection} places={places} onChange={update} data-sentry-element="Dashboard" data-sentry-component="ClimateMetricsTool" data-sentry-source-file="ClimateMetricsTool.tsx" />;
};
interface Props {
  build: string;
  summary: Summary;
  selection: Selection;
  places: PointPlace[];
  onChange: (change: Partial<Selection> | ((s: Selection) => Selection)) => void;
}
const Dashboard = ({
  build,
  summary,
  selection,
  places,
  onChange
}: Props) => {
  const dark = useDark();
  const metric = metricOf(summary, selection.metric);
  const reason = mapReason(summary, metric, selection);
  const {
    layer,
    error
  } = useMapLayer(build, summary, metric, selection, reason);
  const countries = useCountryCells(summary);
  const land = useLand(summary);
  const [message, setMessage] = useState("");
  const count = selection.points.length;
  useEffect(() => setMessage(""), [count]);
  const add = useCallback((point: Point) => {
    if (count >= MAX_POINTS) setMessage(FULL_MESSAGE);else onChange(s => addPoint(s, point));
  }, [count, onChange]);
  const at = places.map(p => p.place?.metrics[metric.id]);
  const series = places.map((p, k) => p.place ? seriesOf(summary, metric, selection.vt, at[k]) : null);
  const cells = at.map(a => a?.cell ?? null);
  const failed = places.map(p => p.isError);
  const selected = places[selection.sel];
  return <div className="flex flex-col gap-3 pb-16" data-sentry-component="Dashboard" data-sentry-source-file="ClimateMetricsTool.tsx">
      <Controls summary={summary} selection={selection} onChange={onChange} onAdd={add} message={message} data-sentry-element="Controls" data-sentry-source-file="ClimateMetricsTool.tsx" />
      <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-solid border-gray-200 bg-gray-200 lg:grid-cols-[minmax(0,1fr)_360px]">
        <MapPanel summary={summary} selection={selection} metric={metric} layer={layer} reason={reason} error={error} dark={dark} countries={countries} land={land} onChange={onChange} onAdd={add} data-sentry-element="MapPanel" data-sentry-source-file="ClimateMetricsTool.tsx" />
        <AboutPanel summary={summary} selection={selection} metric={metric} data-sentry-element="AboutPanel" data-sentry-source-file="ClimateMetricsTool.tsx" />
        <SeriesChart summary={summary} selection={selection} metric={metric} series={series[selection.sel] ?? null} none={!!selected?.place && !at[selection.sel]} failed={!!selected?.isError} dark={dark} onChange={onChange} data-sentry-element="SeriesChart" data-sentry-source-file="ClimateMetricsTool.tsx" />
        <DataTable summary={summary} selection={selection} series={series} cells={cells} failed={failed} loading={places.some(p => p.isLoading)} dark={dark} onChange={onChange} data-sentry-element="DataTable" data-sentry-source-file="ClimateMetricsTool.tsx" />
      </div>
      <p className="m-0 text-[10.5px] leading-normal text-gray-500">
        {summary.countries.attribution} Climate data: CSRD climate metrics cube, snapshot {summary.build.snapshotUtc}.
      </p>
    </div>;
};